import { Component, Input, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Automation } from '@src/app/models/automation';
import { Automationaction } from '@src/app/models/automationaction';
import { ProjectautomationService } from './service/projectautomation.service';
import { SessionHelper } from '@src/app/helpers/sessionHelper';
import { Project } from '@src/app/models/project';
import { AutomationExportImportService } from './service/automation-export-import.service';
import { Data } from '@angular/router';
import { DatastoreService } from '@src/app/helpers/datastore/datastore.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-projectautomation',
  templateUrl: './projectautomation.component.html',
  styleUrl: './projectautomation.component.css'
})
export class ProjectautomationComponent {
  @Input() project: Project;
  create: boolean;
  edit: boolean;
  delete: boolean;
  standard: boolean = true;
  automationlist: Automation[] = [];
  modalAutomation: Automation;
  newAutomationName: string;
  modalAutomationActionList: Automationaction[] = [];
  newActionName: string;
  openActionId: string | null = null;
  collapsemetaboolean: boolean;
  collapseactionboolean: boolean;
  @Input() automationType: string; // Default value for automationType
  @Input() organizationId: string; // Optional input for organizationId 
  showSpinner: boolean;
  conditions: { value: string; viewValue: string }[] = [];

  constructor(
    private pasrv: ProjectautomationService,
    private exportImport: AutomationExportImportService,
    private datastore: DatastoreService
  ) { }

  ngOnInit() {

  }

  // Ensure you have the currently selected automation in `modalAutomation` (as in your code).
  async onExportAutomation() {
    try {
      const orgId = SessionHelper.getChoosenOrg();
      if (!this.modalAutomation?.automationId) { return; }
      const payload = await this.exportImport.buildExportPayload(orgId, this.modalAutomation);
      const base = (this.modalAutomation.automationname || 'automation').replace(/[^\w\-]+/g, '_');
      this.exportImport.download(`${base}_export.json`, payload);
    } catch (e) {
      console.error('Export failed', e);
      alert(`Export failed: ${(e as Error).message}`);
    }
  }

  async onImportFileSelected(evt: Event) {
    const input = evt.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    try {
      const newId = await this.exportImport.importIntoCurrentOrg(file, {
        projectId: this.project?.projectId // if this page is scoped to a project, import as a project automation
      });
      // Optionally refresh list
      this.refreshAutomationsList();
      alert('Import complete.');
    } catch (e) {
      console.error('Import failed', e);
      alert(`Import failed: ${(e as Error).message}`);
    } finally {
      input.value = ''; // reset
    }
  }

  private refreshAutomationsList() {
    // Whatever you already do today to refresh after create/delete
    const orgId = SessionHelper.getChoosenOrg();
    // Example:
    this.pasrv.getAutomations(orgId, this.project?.projectId).subscribe(list => this.automationlist = list);
  }

  async loadConditions(triggerType: string) {
    //Trigger type task.done
    if (triggerType === 'task.done') {
      const tasks = await firstValueFrom(this.datastore.getProjectTasks(this.project.projectId, new Date().toISOString()));
      if (tasks.length > 0) {
        return [{ value: 'any', viewValue: 'Any' }, ...tasks.map(task => ({ value: task.taskId.split('_')[0], viewValue: task.title }))];
      } else {
        return [{ value: 'any', viewValue: 'Any' }];
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['project'] && changes['project'].currentValue) {
      this.project = changes['project'].currentValue;
      this.pasrv.getAutomations(SessionHelper.getChoosenOrg(), this.project.projectId).subscribe(
        {
          next: (data: Automation[]) => {
            //console.log('Automations fetched on project change:', data);
            this.automationlist = data;
          },
          error: (error) => {
            console.error('Error fetching automations on project change:', error);
          }
        }
      );
    }
    if (changes['organizationId'] && changes['organizationId'].currentValue) {
      this.organizationId = changes['organizationId'].currentValue;
      this.pasrv.getAutomations(this.organizationId).subscribe(
        {
          next: (data: Automation[]) => {
            //console.log('Automations fetched on organization change:', data);
            this.automationlist = data.filter(auto => auto.automationtype === this.automationType);
          },
          error: (error) => {
            console.error('Error fetching automations on organization change:', error);
          }
        }
      );
    }
  }
  createAction() {
    let newAction = new Automationaction();
    newAction.automationId = this.modalAutomation.automationId;
    newAction.projectId = this.project ? this.project.projectId : SessionHelper.getChoosenOrg();
    newAction.organizationId = SessionHelper.getChoosenOrg();
    newAction.status = 'active'; // Default status
    newAction.created = new Date().toISOString(); // Set current date as created date
    newAction.createdby = SessionHelper.getUserSub(); // Set the current user as creator
    newAction.name = this.newActionName; // Use the input name for the action
    this.pasrv.createAutomationAction(SessionHelper.getChoosenOrg(), this.modalAutomation.automationId, newAction).subscribe(
      {
        next: (data: Automationaction) => {
          //console.log('Automation action created:', data);
          newAction.actionId = data.actionId; // Assign the actionId returned from the server
          this.modalAutomationActionList.push(newAction);
          this.newActionName = ''; // Reset the input field after creation
        },
        error: (error) => {
          console.error('Error creating automation action:', error);
        }
      }
    );
  }
  createAuto() {
    this.create = !this.create
    this.standard = !this.standard
    this.modalAutomation = new Automation();
    //If no project asume organization automation and set organizationId
    this.modalAutomation.projectId = this.project ? this.project.projectId : SessionHelper.getChoosenOrg();
    this.modalAutomation.automationname = this.newAutomationName;
    this.modalAutomation.automationtype = this.automationType; // Use the automationType input
    this.modalAutomation.organizationId = SessionHelper.getChoosenOrg();
  }

  postNewAuto() {
    //console.log('Creating new automation with type:', this.automationType);
    this.modalAutomation.automationtype = this.automationType; // Use the automationType input
    this.modalAutomation.organizationId = SessionHelper.getChoosenOrg();
    this.modalAutomation.projectId = this.project ? this.project.projectId : SessionHelper.getChoosenOrg();
    //this.modalAutomation.automationtriggercondition = 'all'; // Default condition
    this.showSpinner = false;

    this.pasrv.createAutomation(SessionHelper.getChoosenOrg(), this.modalAutomation).subscribe(
      {
        next: (data: Automation) => {

          this.automationlist.push(data);
          // Edit mode continues on the created automation, which now has its id
          this.modalAutomation = data;
          this.autoResetcreate();
        },
        error: (error) => {
          console.error('Error creating automation:', error);
        }
      }
    );
  }

  collapsemeta() {
    this.collapsemetaboolean = !this.collapsemetaboolean;
  }

  collapseaction() {
    this.collapseactionboolean = !this.collapseactionboolean;
  }

  setAutomationCollapse(actionId: string) {
    this.openActionId = this.openActionId === actionId ? null : actionId;
  }

  putAuto(auto) {
    this.showSpinner = true;

    auto.type = this.automationType; // Use the automationType input
    auto.projectId = this.project && this.project.projectId ? this.project.projectId : SessionHelper.getChoosenOrg();
    // auto.automationtriggercondition = 'all'; // Default condition

    this.pasrv
      .updateAutomation(SessionHelper.getChoosenOrg(), auto)
      .subscribe({
        next: (data: Automation) => {


          const index = this.automationlist.findIndex(
            a => a.automationId === data.automationId
          );
          if (index !== -1) {
            this.automationlist[index] = data;
          }
          this.showSpinner = false;
          this.autoReset();
        },
        error: error => {
          console.error('Error updating automation:', error);
          this.showSpinner = false;
        }
      });
  }


  deleteAutoConfirm() {
    this.pasrv
      .deleteAutomation(
        SessionHelper.getChoosenOrg(),
        this.modalAutomation.automationId,
        this.modalAutomation.projectId
      )
      .subscribe({
        //delete the trigger
        next: () => {


          this.automationlist = this.automationlist.filter(
            a => a.automationId !== this.modalAutomation.automationId
          );
          this.autoReset();
        },
        error: error => {
          console.error('Error deleting automation:', error);
        }
      });
  }


  autoReset() {
    this.delete = false;
    this.create = false;
    this.edit = false;
    this.standard = true;
    this.modalAutomation = null;
    this.newActionName = '';
    this.openActionId = null;
    this.showSpinner = false;
  }

  autoResetcreate() {
    this.delete = false;
    this.create = false;
    this.edit = true;
    this.standard = false;
    this.openActionId = null;
    this.showSpinner = false;
  }

  async editAuto(auto) {
    this.pasrv.getAutomationActions(SessionHelper.getChoosenOrg(), auto.automationId).subscribe(
      {
        next: (data: Automationaction[]) => {
          //console.log('Automation actions fetched:', data);
          //Sort actions by sortorder
          data.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

          this.modalAutomationActionList = data;
          this.openActionId = null;
          //console.log('Actions:', this.modalAutomationActionList);
        },
        error: (error) => {
          console.error('Error fetching automation actions:', error);
        }
      }
    );

    this.modalAutomation = auto;
    this.conditions = await this.loadConditions(auto.automationtriggerevent);
    this.edit = !this.edit
    this.standard = !this.standard
  }

  deleteAuto(auto) {
    //console.log('Deleting automation:', auto);
    this.modalAutomation = auto;
    this.delete = !this.delete
    this.standard = !this.standard
  }

}
